
// ─── Shared styles — earthy orange palette ────────────────────────────
const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid #7a2e00', fontFamily:"'Georgia',serif", color:'#1a0e06', lineHeight:'1.7', background:'#fdf6ee' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c45a10', paddingBottom:'8px', marginBottom:'24px', color:'#7a2e00' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#a04010', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c45a10', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#fdf0e4', border:'2px solid #c45a10', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New',monospace", fontSize:'0.88rem', color:'#7a2e00', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c45a10' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#a04010', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf4ec' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf6ee' },
  tdL:          { fontWeight:'600', color:'#7a2e00', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New',monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fdf4ec', border:'1px solid #e8b07a', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7a2e00', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#7a4a28', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((it,i)=><div key={i} style={S.infoBoxItem}>{it}</div>)}
    </div>
  );
}
function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{...S.th,width:'220px'}}>Concept</th>
        <th style={S.th}>Detail</th>
      </tr></thead>
      <tbody>{rows.map(([l,v],i)=>(
        <tr key={i}>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.tdL}}>{l}</td>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.code}}>{v}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

// ─── Cycloidal disc profile (parametric epitrochoid) ──────────────────
// R=pin-circle radius  Rr=pin radius  E=eccentricity  N=lobes  phase=disc rotation
function cycloidPts(cx, cy, R, Rr, E, N, phase=0, steps=280) {
  const pts=[];
  for(let i=0;i<=steps;i++){
    const t=(2*Math.PI*i)/steps;
    const psi=Math.atan2(Math.sin((1-N)*t),(R/(E*(N+1)))-Math.cos((1-N)*t));
    const x=R*Math.cos(t)-Rr*Math.cos(t+psi)-E*Math.cos(N*t);
    const y=-R*Math.sin(t)+Rr*Math.sin(t+psi)+E*Math.sin(N*t);
    const rx=x*Math.cos(phase)-y*Math.sin(phase);
    const ry=x*Math.sin(phase)+y*Math.cos(phase);
    pts.push(`${(cx+rx).toFixed(1)},${(cy+ry).toFixed(1)}`);
  }
  return 'M '+pts.join(' L ')+' Z';
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Full Anatomy
// cx=305 keeps gear (outer r=132) left edge at 173 → room for labels at x≥10
// Info panel starts at x=630, gear right edge=437 → 193 px gap
// ─────────────────────────────────────────────────────────────────────
function DiagramAnatomy() {
  const W=960, H=445;
  const cx=305, cy=210, R=110, Rr=10, E=7, N=9, nPins=N+1;
  const housingR=R+22;
  const dcx=cx+E, dcy=cy; // disc centre at phase=0

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Cycloidal Drive — Full Anatomy: Eccentric Shaft · Disc · Ring Pins · Output Pins</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arCY" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <radialGradient id="hGrad" cx="50%" cy="50%" r="50%">
            <stop offset="65%" stopColor="#fdf4ec"/>
            <stop offset="100%" stopColor="#e8b07a" stopOpacity="0.5"/>
          </radialGradient>
        </defs>

        {/* Housing ring */}
        <circle cx={cx} cy={cy} r={housingR} fill="url(#hGrad)" stroke="#7a2e00" strokeWidth="3"/>

        {/* Ring pins */}
        {Array.from({length:nPins},(_,i)=>{
          const a=(2*Math.PI*i)/nPins;
          return <circle key={i} cx={cx+R*Math.cos(a)} cy={cy+R*Math.sin(a)}
            r={Rr} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>;
        })}

        {/* Cycloidal disc */}
        <path d={cycloidPts(dcx,dcy,R,Rr,E,N,0)}
          fill="#c45a10" stroke="#7a2e00" strokeWidth="2" opacity="0.82"/>

        {/* Output pin holes (6 holes in disc) */}
        {Array.from({length:6},(_,i)=>{
          const a=(2*Math.PI*i)/6, r=38;
          const px=dcx+r*Math.cos(a), py=dcy+r*Math.sin(a);
          return <g key={i}>
            <circle cx={px} cy={py} r={10} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
            <circle cx={px+1.8} cy={py+1.8} r={5.5} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.2"/>
          </g>;
        })}

        {/* Eccentric bearing */}
        <circle cx={dcx} cy={dcy} r={24} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2" opacity="0.88"/>
        <circle cx={dcx} cy={dcy} r={11} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>

        {/* Input shaft centre (true centre) */}
        <circle cx={cx} cy={cy} r={5} fill="#7a2e00"/>
        {/* Eccentricity line */}
        <line x1={cx} y1={cy} x2={dcx} y2={dcy} stroke="#c45a10" strokeWidth="2" strokeDasharray="3,2"/>

        {/* Output shaft */}
        <circle cx={cx} cy={cy} r={20} fill="#fdf0e4" stroke="#7a2e00" strokeWidth="2.5"/>
        <text x={cx} y={cy+4} textAnchor="middle" fontSize="8" fontWeight="700" fill="#7a2e00">OUT</text>

        {/* Input CW arrow */}
        <path d={`M ${cx+35} ${cy-14} A 38 38 0 0 1 ${cx+14} ${cy-35}`}
          fill="none" stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arCY)"/>
        <text x={cx+52} y={cy-26} fontSize="9" fontWeight="700" fill="#c45a10">ω_in (fast)</text>

        {/* Output CCW arrow */}
        <path d={`M ${cx-26} ${cy+10} A 32 32 0 0 0 ${cx-10} ${cy+30}`}
          fill="none" stroke="#7a2e00" strokeWidth="2" strokeDasharray="5,3" markerEnd="url(#arCY)"/>
        <text x={cx-28} y={cy+housingR+28} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">ω_out (slow, reversed)</text>

        {/* ── Callout labels — all outside the gear ────────────────────
             Gear outer radius = housingR = 132
             Left edge = cx − 132 = 173  →  labels at x ≥ 10 OK
             Bottom = cy + 132 = 342     →  labels below at y ≤ H=445 OK
             Right edge = cx + 132 = 437 →  labels right at x ≤ 625 OK (panel at 630) */}

        {/* Housing/ring pins — TOP, centred */}
        <line x1={cx} y1={cy-housingR} x2={cx} y2={cy-housingR-30} stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={cx-95} y={cy-housingR-60} width={190} height={30} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={cx} y={cy-housingR-42} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Ring Gear Housing</text>
        <text x={cx} y={cy-housingR-30} textAnchor="middle" fontSize="8.5" fill="#a04010">{nPins} stationary ring pins · fixed to frame</text>

        {/* Cycloidal disc — LEFT side */}
        {/* Leader from gear left edge to label box at x=10 */}
        <line x1={cx-housingR+8} y1={cy-20} x2={172} y2={cy-32} stroke="#c45a10" strokeWidth="1.2"/>
        <rect x={10} y={cy-58} width={160} height={30} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={90} y={cy-40} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Cycloidal Disc</text>
        <text x={90} y={cy-28} textAnchor="middle" fontSize="8.5" fill="#a04010">{N} lobes · driven by eccentric</text>

        {/* Eccentric bearing — RIGHT, upper half */}
        <line x1={cx+housingR-8} y1={cy-30} x2={cx+housingR+42} y2={cy-42} stroke="#c45a10" strokeWidth="1.2"/>
        <rect x={cx+housingR+40} y={cy-68} width={148} height={30} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={cx+housingR+114} y={cy-50} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Eccentric Bearing</text>
        <text x={cx+housingR+114} y={cy-38} textAnchor="middle" fontSize="8.5" fill="#a04010">offset E={E} mm · input shaft</text>

        {/* Output pins — BOTTOM */}
        <line x1={cx+40} y1={cy+housingR-8} x2={cx+40} y2={cy+housingR+32} stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={cx-40} y={cy+housingR+30} width={180} height={32} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={cx+50} y={cy+housingR+48} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Output Pins + Holes</text>
        <text x={cx+50} y={cy+housingR+58} textAnchor="middle" fontSize="8.5" fill="#a04010">transmit rotation · hole dia = pin dia + 2E</text>

        {/* ── Right facts panel ── x=630 → 948, safely clear of gear right=437 */}
        <rect x={630} y={15} width={318} height={408} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={789} y={34} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Fundamental Relationships</text>
        <line x1={638} y1={40} x2={940} y2={40} stroke="#e8b07a"/>
        {[
          ['Components:','Eccentric input shaft'],
          ['','Cycloidal disc (1 or 2)'],
          ['','Ring gear housing + pins'],
          ['','Output shaft + pins & holes'],
          ['',''],
          ['Tooth counts:','N_pins = N_lobes + 1'],
          ['','(always 1 more ring pin)'],
          ['Ratio formula:','i = −N_lobes'],
          ['','(−ve: output reverses)'],
          ['e.g. 9 lobes:','i = −9 : 1'],
          ['e.g. 59 lobes:','i = −59 : 1'],
          ['',''],
          ['Single stage:','3:1 to 119:1'],
          ['Double stage:','up to 7,569:1'],
          ['',''],
          ['Contact ratio:','~50% of pins engaged'],
          ['','simultaneously'],
          ['Shock capacity:','5× rated torque'],
          ['',''],
          ['Efficiency:','90–93% per stage'],
          ['Backlash:','Near zero (precision)'],
          ['Coaxial:','Input & output same axis'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={640} y={56+i*15} fontSize="9" fontWeight={l?'700':'400'} fill="#7a2e00">{l}</text>
            <text x={742} y={56+i*15} fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Five key parts: eccentric input shaft · cycloidal disc(s) · stationary ring pins · output pins · output shaft — all coaxial, all rigid metal</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Operating principle: 4-step sequence
// Each step circle radius = 76 + 14 = 90px; step cx = 95,290,485,680
// Label boxes 136px wide centred on each cx — fits within step columns
// ─────────────────────────────────────────────────────────────────────
function DiagramPrinciple() {
  const W=900, H=315;
  const R=72, Rr=7, E=5, N=8, nPins=N+1;

  function Step({cx,cy,phase,label,note,highlight}) {
    const dcx=cx+E*Math.cos(phase), dcy=cy+E*Math.sin(phase);
    return (
      <g>
        <circle cx={cx} cy={cy} r={R+14} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2"/>
        {Array.from({length:nPins},(_,i)=>{
          const a=(2*Math.PI*i)/nPins;
          const inH = highlight.includes(i);
          return <circle key={i} cx={cx+R*Math.cos(a)} cy={cy+R*Math.sin(a)}
            r={Rr} fill={inH?'#7a2e00':'#d4874a'} stroke="#7a2e00" strokeWidth={inH?2:1.2}/>;
        })}
        <path d={cycloidPts(dcx,dcy,R,Rr,E,N,phase)}
          fill="#c45a10" stroke="#7a2e00" strokeWidth="1.8" opacity="0.82"/>
        {/* eccentric dot + centre */}
        <circle cx={dcx} cy={dcy} r={7} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
        <circle cx={cx} cy={cy} r={4} fill="#7a2e00"/>
        <line x1={cx} y1={cy} x2={dcx} y2={dcy} stroke="#c45a10" strokeWidth="1.5" strokeDasharray="3,2"/>
        {/* label */}
        <rect x={cx-66} y={cy+R+18} width={132} height={36} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={cx} y={cy+R+32} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">{label}</text>
        <text x={cx} y={cy+R+45} textAnchor="middle" fontSize="7.5" fill="#a04010">{note}</text>
      </g>
    );
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Operating Principle — Each 360° Input Revolution Moves Disc Back One Lobe</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arOP" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>
        <Step cx={95}  cy={126} phase={0}           label="① 0° — Start"      note="Eccentric right · lobe at 3 o'clock"  highlight={[0,1,4]}/>
        <Step cx={295} cy={126} phase={Math.PI/2}   label="② 90° CW Input"    note="Disc orbits up · rotated −45°"         highlight={[2,3,6]}/>
        <Step cx={495} cy={126} phase={Math.PI}     label="③ 180° CW Input"   note="Eccentric left · rotated −90°"         highlight={[4,5,0]}/>
        <Step cx={695} cy={126} phase={3*Math.PI/2} label="④ 270° CW Input"   note="Disc orbits down · rotated −135°"      highlight={[6,7,2]}/>
        {/* Arrows between steps */}
        {[183,383,583].map((x,i)=>(
          <line key={i} x1={x} y1={126} x2={x+20} y2={126} stroke="#c45a10" strokeWidth="2" markerEnd="url(#arOP)"/>
        ))}
        {/* Summary bar */}
        <rect x={10} y={290} width={880} height={20} rx={4} fill="#fdf0e4" stroke="#e8b07a"/>
        <text x={450} y={303} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">
          N=8 lobes: 360° input → disc rotates −45° (one lobe) · 8 full input revs needed for 1 output rev → 8:1 ratio
        </text>
      </svg>
      <div style={S.dCaption}>The disc orbits without spinning freely — ring pins force it backward one lobe for each input revolution · dark pins show the contact zone shifting continuously</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Disc geometry & parametric equations
// Left: disc at cx=170, housingR≈122 → left edge 48 > 10 ✓
//   right of gear = 170+122=292; equation panel starts at 310 → 18px gap ✓
// ─────────────────────────────────────────────────────────────────────
function DiagramGeometry() {
  const W=960, H=320;
  const cx=170, cy=160, R=95, Rr=9, E=6, N=7, nPins=N+1;
  const housingR=R+14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Disc Profile Geometry — Parametric Epitrochoid &amp; Key Design Parameters</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arGM" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arGMs" markerWidth="7" markerHeight="7" refX="2" refY="3.5" orient="auto">
            <polygon points="6 0,0 3.5,6 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* Housing */}
        <circle cx={cx} cy={cy} r={housingR} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2.5"/>
        {/* Pin pitch circle (dashed) */}
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="#e8b07a" strokeWidth="1.5" strokeDasharray="5,3"/>
        {/* Ring pins */}
        {Array.from({length:nPins},(_,i)=>{
          const a=(2*Math.PI*i)/nPins;
          return <circle key={i} cx={cx+R*Math.cos(a)} cy={cy+R*Math.sin(a)} r={Rr}
            fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>;
        })}
        {/* Disc */}
        <path d={cycloidPts(cx+E,cy,R,Rr,E,N)} fill="#c45a10" stroke="#7a2e00" strokeWidth="2" opacity="0.82"/>

        {/* R dimension: from centre to pin circle (angle 25°) */}
        {(()=>{
          const a=25*Math.PI/180;
          return <g>
            <line x1={cx} y1={cy} x2={cx+R*Math.cos(a)} y2={cy+R*Math.sin(a)}
              stroke="#e8b07a" strokeWidth="1.5" strokeDasharray="4,2" markerEnd="url(#arGM)"/>
            <text x={cx+R*0.52*Math.cos(a)+6} y={cy+R*0.52*Math.sin(a)-4}
              fontSize="9" fontWeight="700" fill="#a04010">R</text>
            <text x={cx+R*0.52*Math.cos(a)+6} y={cy+R*0.52*Math.sin(a)+7}
              fontSize="7.5" fill="#7a4a28">pin circle</text>
          </g>;
        })()}

        {/* E dimension: from centre to disc centre */}
        <line x1={cx} y1={cy} x2={cx+E} y2={cy}
          stroke="#7a2e00" strokeWidth="2" markerEnd="url(#arGM)" markerStart="url(#arGMs)"/>
        <text x={cx+E+4} y={cy-5} fontSize="8.5" fontWeight="700" fill="#7a2e00">E (eccentricity)</text>

        {/* Rr dimension: on one pin (at angle 25°) */}
        {(()=>{
          const a=25*Math.PI/180;
          const px=cx+R*Math.cos(a), py=cy+R*Math.sin(a);
          return <g>
            <line x1={px} y1={py} x2={px+Rr*1.2} y2={py}
              stroke="#c45a10" strokeWidth="1.5" markerEnd="url(#arGM)" markerStart="url(#arGMs)"/>
            <text x={px+Rr*1.2+3} y={py+4} fontSize="8" fontWeight="700" fill="#c45a10">Rr</text>
          </g>;
        })()}

        {/* Output pin hole callout at bottom-right of disc */}
        {(()=>{
          const a=-Math.PI*0.35, r=34;
          const px=cx+E+r*Math.cos(a), py=cy+r*Math.sin(a);
          return <g>
            <circle cx={px} cy={py} r={11} fill="none" stroke="#7a2e00" strokeWidth="1.5" strokeDasharray="3,2"/>
            <circle cx={px+2} cy={py+2} r={5} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.2"/>
            <line x1={px+11} y1={py} x2={cx+housingR+12} y2={py+18} stroke="#7a2e00" strokeWidth="1.2"/>
            <text x={cx+housingR+14} y={py+14} fontSize="8" fontWeight="700" fill="#7a2e00">Output pin hole</text>
            <text x={cx+housingR+14} y={py+25} fontSize="7.5" fill="#a04010">d_hole = d_pin + 2E</text>
          </g>;
        })()}

        {/* Equations panel: x=310, width=640, right edge=950 within W=960 */}
        <rect x={310} y={10} width={640} height={300} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={630} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Profile Parametric Equations  (θ: 0 → 2π)</text>
        <line x1={318} y1={34} x2={942} y2={34} stroke="#e8b07a"/>
        {[
          ['X(θ) =','R·cos(θ) − Rr·cos(θ+ψ) − E·cos(N·θ)'],
          ['Y(θ) =','−R·sin(θ) + Rr·sin(θ+ψ) + E·sin(N·θ)'],
          ['',''],
          ['ψ =','arctan(  sin((1−N)θ)  /'],
          ['','      ( R/(E(N+1)) − cos((1−N)θ) )  )'],
          ['',''],
          ['Parameter','Meaning'],
          ['R','Radius of ring pin pitch circle (mm)'],
          ['Rr','Ring pin roller radius (mm)'],
          ['E','Eccentricity of input shaft (mm)'],
          ['N','Lobe count on disc  (= ring pins − 1)'],
          ['θ','Input angle parameter (0 → 2π)'],
          ['ψ','Auxiliary profile tangency angle'],
          ['',''],
          ['Design rules:','E < Rr  (required — ensures no undercutting)'],
          ['','Typical E/Rr ratio: 0.3 – 0.7'],
          ['',''],
          ['Shortened cycloid:','Multiply X, Y by factor K < 1'],
          ['','Reduces eccentricity & vibration at high speed'],
          ['','Small reduction in contact ratio / load capacity'],
          ['',''],
          ['Output pin hole:','d_hole = d_output_pin + 2·E  (exact)'],
          ['','Disc orbits at radius E — hole must clear that orbit'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={320} y={46+i*11} fontSize="8.5"
              fontWeight={i===6||i===14?'700':'400'}
              fontStyle={i===0||i===1||i===3||i===4?'normal':'normal'}
              fill={i===6?'#7a2e00':l&&i>5?'#7a4a28':'#7a4a28'}>{l}</text>
            <text x={l&&i<6?398:(l&&i===6?440:l&&i>5?400:320)} y={46+i*11}
              fontSize="8.5" fontFamily={i<6||i===14||i===17||i===21?"'Courier New',monospace":undefined}
              fontWeight={i===6||i===14?'700':'400'}
              fill={i<6?'#c45a10':i===6?'#7a2e00':'#a04010'}>{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The disc profile is an epitrochoid — the only curve that gives simultaneous conjugate contact with every ring pin at its own radius</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Gear ratio derivation + bar chart
// Derivation panel: x=10 w=310. Bar chart: x=340 to 890. W=900 total.
// ─────────────────────────────────────────────────────────────────────
function DiagramRatio() {
  const W=900, H=295;
  const bars=[
    {N:3, r:3},{N:5,r:5},{N:8,r:8},{N:11,r:11},{N:17,r:17},
    {N:35,r:35},{N:59,r:59},{N:99,r:99},{N:119,r:119},
  ];
  const barBase=195, maxH=130, maxR=119, barW=56, startX=350;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Gear Ratio — Derivation, Formula &amp; Available Range</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Derivation panel */}
        <rect x={10} y={10} width={320} height={278} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={170} y={27} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Ratio Formula</text>
        <line x1={18} y1={33} x2={322} y2={33} stroke="#e8b07a"/>
        {[
          ['Single stage:',''],
          ['i = −N_lobes','← output reverses'],
          ['i = −(N_pins − 1)',''],
          ['',''],
          ['Example: 9 lobes / 10 pins',''],
          ['i = −9 : 1',''],
          ['ω_in=1000 rpm→ω_out=−111','rpm'],
          ['τ_out=τ_in × 9 × η',''],
          ['',''],
          ['Double stage:',''],
          ['i_total = i₁ × i₂',''],
          ['(both reversals cancel)','same dir as input'],
          ['9×9 =','81:1'],
          ['87×87 =','7,569:1 (max)'],
          ['',''],
          ['Max single stage:','119:1 (120 pins)'],
          ['',''],
          ['vs Planetary (same OD):',''],
          ['Planetary single stage:','~10:1'],
          ['Cycloidal:','up to 119:1'],
          ['Cycloidal advantage:','~10× more range'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={18} y={44+i*11} fontSize="9"
              fontWeight={l&&!v&&i>0||i===0||i===9||i===17?'700':'400'}
              fill={l.includes('119')||l.includes('advantage')?'#c45a10':l?'#7a2e00':'#7a4a28'}>{l}</text>
            <text x={l?178:18} y={44+i*11} fontSize="9"
              fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}

        {/* Bar chart axes */}
        <line x1={startX-8} y1={45} x2={startX-8} y2={barBase+2} stroke="#e8b07a" strokeWidth="1.5"/>
        <line x1={startX-8} y1={barBase+2} x2={892} y2={barBase+2} stroke="#e8b07a" strokeWidth="1.5"/>
        {[0,20,40,60,80,100,119].map((v,i)=>{
          const y=barBase-(v/maxR)*maxH;
          return <g key={i}>
            <line x1={startX-12} y1={y} x2={892} y2={y} stroke="#e8c89a" strokeWidth="0.7" strokeDasharray="3,3"/>
            <text x={startX-14} y={y+4} textAnchor="end" fontSize="7.5" fill="#7a4a28">{v}</text>
          </g>;
        })}
        <text x={startX-8} y={36} textAnchor="middle" fontSize="8" fill="#7a2e00"
          transform={`rotate(-90,${startX-8},36)`}>Ratio</text>
        <text x={615} y={barBase+22} textAnchor="middle" fontSize="8.5" fill="#7a4a28">N_lobes (disc lobe count)</text>

        {bars.map((d,i)=>{
          const h=(d.r/maxR)*maxH;
          const x=startX+i*57;
          return <g key={i}>
            <rect x={x} y={barBase-h} width={barW} height={h} fill="#c45a10" opacity={0.44+i*0.057} rx={3}/>
            <text x={x+barW/2} y={barBase-h-5} textAnchor="middle" fontSize="8" fontWeight="700" fill="#7a2e00">{d.r}</text>
            <text x={x+barW/2} y={barBase+13} textAnchor="middle" fontSize="7.5" fill="#a04010">{d.N}</text>
          </g>;
        })}
      </svg>
      <div style={S.dCaption}>Ratio equals the lobe count — add one lobe+pin pair to increase ratio by 1, with no change to housing diameter or shaft size</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Dual-disc balancing
// Single disc panel: x=10 w=260. Dual disc panel: x=285 w=420.
// Info panel: x=720 w=175; right edge=895 within W=900.
// ─────────────────────────────────────────────────────────────────────
function DiagramDualDisc() {
  const W=900, H=310;
  const R=75, Rr=7, E=5, N=8, nPins=N+1;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Single vs Dual Disc — Imbalance Cancellation &amp; Load Sharing</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arDD" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arDDr" markerWidth="7" markerHeight="7" refX="2" refY="3.5" orient="auto">
            <polygon points="6 0,0 3.5,6 7" fill="#c0392b"/>
          </marker>
        </defs>

        {/* ── SINGLE DISC panel ── */}
        <rect x={10} y={10} width={260} height={292} rx={6} fill="none" stroke="#e8b07a" strokeDasharray="5,3" strokeWidth="1.5"/>
        <text x={140} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Single Disc</text>
        <text x={140} y={40} textAnchor="middle" fontSize="8.5" fill="#a04010">Eccentric → net imbalance force</text>
        {(()=>{
          const cx=140, cy=152;
          return <g>
            <circle cx={cx} cy={cy} r={R+13} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2"/>
            {Array.from({length:nPins},(_,i)=>{
              const a=(2*Math.PI*i)/nPins;
              return <circle key={i} cx={cx+R*Math.cos(a)} cy={cy+R*Math.sin(a)} r={Rr}
                fill="#d4874a" stroke="#7a2e00" strokeWidth="1.2"/>;
            })}
            <path d={cycloidPts(cx+E,cy,R,Rr,E,N)} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.8" opacity="0.82"/>
            <circle cx={cx+E} cy={cy} r={7} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
            <circle cx={cx} cy={cy} r={4} fill="#7a2e00"/>
            {/* imbalance force arrow */}
            <line x1={cx+E} y1={cy} x2={cx+E+32} y2={cy-22}
              stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#arDD)"/>
            <text x={cx+E+35} y={cy-24} fontSize="8.5" fontWeight="700" fill="#c0392b">F_imbalance</text>
            <text x={cx} y={cy+R+28} textAnchor="middle" fontSize="8" fill="#7a4a28">Vibration at ω_in</text>
            <text x={cx} y={cy+R+40} textAnchor="middle" fontSize="8" fill="#7a4a28">Limits max speed</text>
          </g>;
        })()}

        {/* ── DUAL DISC panel ── */}
        <rect x={285} y={10} width={420} height={292} rx={6} fill="none" stroke="#c45a10" strokeDasharray="5,3" strokeWidth="1.8"/>
        <text x={495} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Dual Disc (180° offset) — Standard Configuration</text>
        <text x={495} y={40} textAnchor="middle" fontSize="8.5" fill="#c45a10">Forces cancel · higher torque · smoother at speed</text>
        {(()=>{
          const cx1=385, cx2=605, cy=152;
          return <g>
            {/* Disc 1 */}
            <circle cx={cx1} cy={cy} r={R+12} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.8" opacity="0.9"/>
            {Array.from({length:nPins},(_,i)=>{
              const a=(2*Math.PI*i)/nPins;
              return <circle key={i} cx={cx1+R*Math.cos(a)} cy={cy+R*Math.sin(a)} r={Rr}
                fill="#d4874a" stroke="#7a2e00" strokeWidth="1.2"/>;
            })}
            <path d={cycloidPts(cx1+E,cy,R,Rr,E,N)} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.8" opacity="0.8"/>
            <circle cx={cx1+E} cy={cy} r={7} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
            <circle cx={cx1} cy={cy} r={4} fill="#7a2e00"/>
            {/* Disc 2 at 180° */}
            <circle cx={cx2} cy={cy} r={R+12} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.8" opacity="0.9"/>
            {Array.from({length:nPins},(_,i)=>{
              const a=(2*Math.PI*i)/nPins;
              return <circle key={i} cx={cx2+R*Math.cos(a)} cy={cy+R*Math.sin(a)} r={Rr}
                fill="#d4874a" stroke="#7a2e00" strokeWidth="1.2"/>;
            })}
            <path d={cycloidPts(cx2-E,cy,R,Rr,E,N,Math.PI)} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.8" opacity="0.65"/>
            <circle cx={cx2-E} cy={cy} r={7} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
            <circle cx={cx2} cy={cy} r={4} fill="#7a2e00"/>
            {/* Cancelling arrows */}
            <line x1={cx1+E} y1={cy} x2={cx1+E+28} y2={cy-18} stroke="#c45a10" strokeWidth="2" markerEnd="url(#arDD)"/>
            <line x1={cx2-E} y1={cy} x2={cx2-E-28} y2={cy-18} stroke="#c45a10" strokeWidth="2" markerEnd="url(#arDD)"/>
            <text x={(cx1+cx2)/2} y={cy-28} textAnchor="middle" fontSize="9" fontWeight="700" fill="#c45a10">Equal & opposite → net ≈ 0</text>
            <text x={cx1} y={cy+R+26} textAnchor="middle" fontSize="8" fill="#7a4a28">Disc 1 (phase 0°)</text>
            <text x={cx2} y={cy+R+26} textAnchor="middle" fontSize="8" fill="#7a4a28">Disc 2 (phase 180°)</text>
            <text x={(cx1+cx2)/2} y={cy+R+40} textAnchor="middle" fontSize="8.5" fill="#7a4a28">Double-eccentric shaft · static imbalance cancelled</text>
          </g>;
        })()}

        {/* ── Info panel ── x=720, width=175, right edge=895 */}
        <rect x={720} y={10} width={175} height={292} rx={5} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={807} y={27} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">Dual Disc Benefits</text>
        <line x1={728} y1={33} x2={887} y2={33} stroke="#e8b07a"/>
        {[
          ['Vibration:','Cancelled (static)'],
          ['','Small dynamic'],
          ['','imbalance remains'],
          ['','(acceptable)'],
          ['Load:','Shared equally'],
          ['','→ 2× torque capacity'],
          ['Max speed:','~2× vs single'],
          ['',''],
          ['3-disc option:','For very high speed'],
          ['','Centre disc = 2× mass'],
          ['','Outer pair opposite'],
          ['','Corrects dynamic'],
          ['','imbalance too'],
          ['',''],
          ['Crankshaft var:','3 cranks (RV style)'],
          ['','Symmetric load path'],
          ['','Higher stiffness'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={728} y={44+i*14} fontSize="8.5" fontWeight={l?'700':'400'} fill={l?'#7a2e00':'#7a4a28'}>{l}</text>
            <text x={l?775:728} y={44+i*14} fontSize="8.5" fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Dual-disc with 180° phase offset is the industry standard — the double eccentric shaft ensures both discs always push in opposite directions, cancelling centrifugal imbalance</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Multi-tooth contact & load comparison
// Left gear at cx=170, housingR=115 → left edge=55 ✓, right=285
// Right panel: x=310, width=580, right edge=890 within W=900 ✓
// ─────────────────────────────────────────────────────────────────────
function DiagramLoadShare() {
  const W=900, H=290;
  const cx=170, cy=142, R=95, Rr=8, E=6, N=9, nPins=N+1;
  const contactSet=new Set([0,1,2,4,9]); // ~half

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Multi-Tooth Contact &amp; Load Sharing — ~50% of Pins Engaged Simultaneously</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arLS" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#c45a10"/>
          </marker>
        </defs>

        {/* Housing */}
        <circle cx={cx} cy={cy} r={R+15} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2.5"/>
        {/* Disc */}
        <path d={cycloidPts(cx+E,cy,R,Rr,E,N)} fill="#c45a10" stroke="#7a2e00" strokeWidth="2" opacity="0.82"/>
        {/* Pins with contact highlighting */}
        {Array.from({length:nPins},(_,i)=>{
          const a=(2*Math.PI*i)/nPins;
          const px=cx+R*Math.cos(a), py=cy+R*Math.sin(a);
          const inContact=contactSet.has(i);
          return <g key={i}>
            <circle cx={px} cy={py} r={Rr}
              fill={inContact?'#7a2e00':'#d4874a'} stroke="#7a2e00" strokeWidth={inContact?2:1.2}/>
            {inContact && <line
              x1={px-Rr*1.8*Math.cos(a)} y1={py-Rr*1.8*Math.sin(a)}
              x2={px-Rr*3.5*Math.cos(a)} y2={py-Rr*3.5*Math.sin(a)}
              stroke="#c45a10" strokeWidth="1.8" markerEnd="url(#arLS)"/>}
          </g>;
        })}
        <circle cx={cx+E} cy={cy} r={9} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.8"/>
        <circle cx={cx} cy={cy} r={4} fill="#7a2e00"/>

        {/* Legend below gear */}
        <text x={cx} y={cy+R+32} textAnchor="middle" fontSize="8.5" fill="#7a2e00">
          ● Engaged  ○ Free  — ~{Math.round(nPins/2)}/{nPins} pins in contact
        </text>

        {/* Right comparison panel: x=310 width=580 */}
        <rect x={310} y={10} width={580} height={268} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={600} y={27} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Load Distribution Comparison</text>
        <line x1={318} y1={33} x2={882} y2={33} stroke="#e8b07a"/>

        {/* Table rows */}
        {[
          ['',              'Cycloidal',      'Harmonic',      'Planetary'],
          ['% engaged',     '~50% pins',      '10–30% teeth',  '1–2 teeth'],
          ['Simultaneous',  'N/2 contacts',   '10–30 teeth',   '3 teeth total'],
          ['Load/tooth',    'Low',            'Low–medium',    'High'],
          ['Shock cap.',    '5× rated',       '3× rated',      '2× rated'],
          ['Profile type',  'Epitrochoid',    'Involute',      'Involute'],
          ['Contact type',  'Rolling',        'Roll+slide',    'Roll+slide'],
          ['Hertz stress',  'Low',            'Medium',        'Med–high'],
        ].map((row,ri)=>(
          <g key={ri}>
            <rect x={318} y={36+ri*27} width={566} height={27}
              fill={ri===0?'#a04010':ri%2===0?'#fdf4ec':'#fdf6ee'}/>
            {row.map((cell,ci)=>(
              <text key={ci}
                x={326+ci*138} y={54+ri*27}
                fontSize={ri===0?'9':'8.5'}
                fontWeight={ri===0||ci===0?'700':'400'}
                fill={ri===0?'#fff':ci===1&&ri>0?'#c45a10':ci===0?'#7a2e00':'#7a4a28'}
                fontFamily={ri>0&&ci>0?"'Courier New',monospace":undefined}>
                {cell}
              </text>
            ))}
          </g>
        ))}
        {/* Summary bar */}
        <rect x={318} y={254} width={566} height={20} rx={3} fill="#fdf4ec" stroke="#e8b07a"/>
        <text x={600} y={266} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">
          High contact ratio → distributed load → low per-pin stress → excellent shock resistance (5× rated torque)
        </text>
      </svg>
      <div style={S.dCaption}>~50% of ring pins carry load at any moment (vs 10–30% harmonic, 1–2 teeth planetary) — the dominant reason cycloidal drives tolerate 5× rated torque shock loads</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Efficiency bar chart
// Banner: x=10 w=640 (ends 650, gap before panel at 670). W=960.
// ─────────────────────────────────────────────────────────────────────
function DiagramEfficiency() {
  const W=960, H=255;
  const losses=[
    {label:'Ring Pin\nFriction',    pct:4.0, x:52 },
    {label:'Eccentric\nBearing',    pct:2.5, x:172},
    {label:'Output Pin\nFriction',  pct:2.0, x:292},
    {label:'Lubricant\nChurning',   pct:1.0, x:412},
    {label:'Seal\nFriction',        pct:0.5, x:532},
  ];
  const barBase=175, maxH=110, maxPct=5, barW=80;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Efficiency &amp; Power Loss Sources — Single Stage at Rated Conditions</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Banner — width 640, stops before panel at 670 */}
        <rect x={10} y={8} width={640} height={28} rx={4} fill="#fdf0e4" stroke="#c45a10"/>
        <text x={330} y={21} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">
          Single-stage η ≈ 90–93%  ·  Double stage ≈ 86%  ·  Ratio-independent efficiency
        </text>
        <text x={330} y={31} textAnchor="middle" fontSize="8.5" fill="#a04010">
          η = P_out / P_in = τ_out·ω_out / (τ_in·ω_in)  ·  Better than harmonic; slightly below planetary
        </text>

        {/* Axes */}
        <line x1={34} y1={44} x2={34} y2={barBase+2} stroke="#e8b07a" strokeWidth="1.5"/>
        <line x1={34} y1={barBase+2} x2={655} y2={barBase+2} stroke="#e8b07a" strokeWidth="1.5"/>
        {[0,1,2,3,4,5].map((v,i)=>{
          const y=barBase-(v/maxPct)*maxH;
          return <g key={i}>
            <line x1={30} y1={y} x2={655} y2={y} stroke="#e8c89a" strokeWidth="0.7" strokeDasharray="3,3"/>
            <text x={28} y={y+4} textAnchor="end" fontSize="8" fill="#7a4a28">{v}%</text>
          </g>;
        })}
        <text x={16} y={112} textAnchor="middle" fontSize="9" fill="#7a2e00"
          transform="rotate(-90,16,112)">Loss %</text>

        {losses.map((l,i)=>{
          const h=(l.pct/maxPct)*maxH;
          const lines=l.label.split('\n');
          return <g key={i}>
            <rect x={l.x} y={barBase-h} width={barW} height={h}
              fill="#c45a10" opacity={0.5+i*0.09} rx={3}/>
            <text x={l.x+barW/2} y={barBase-h-6} textAnchor="middle"
              fontSize="10" fontWeight="700" fill="#7a2e00">{l.pct}%</text>
            {lines.map((line,li)=>(
              <text key={li} x={l.x+barW/2} y={barBase+14+li*13}
                textAnchor="middle" fontSize="8.5" fill="#7a4a28">{line}</text>
            ))}
          </g>;
        })}

        {/* Summary panel — x=670 width=282 right edge=952 within W=960 */}
        <rect x={670} y={8} width={282} height={240} rx={5} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={811} y={26} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Efficiency Summary</text>
        <line x1={678} y1={32} x2={944} y2={32} stroke="#e8b07a"/>
        {[
          ['Ring pin friction:', '~4.0%'],
          ['Eccentric bearing:', '~2.5%'],
          ['Output pin fric.:',  '~2.0%'],
          ['Lubricant churning:','~1.0%'],
          ['Seals:',             '~0.5%'],
          ['TOTAL LOSS:',        '~10%'],
          ['Efficiency:',        '~90%'],
          ['',''],
          ['vs Harmonic drive:', '75–85%'],
          ['vs Planetary:',      '94–98%'],
          ['vs Worm gear:',      '50–90%'],
          ['',''],
          ['Lubrication:',       'Oil bath or grease'],
          ['Oil grade:',         'ISO VG 150–320'],
          ['Change interval:',   '20,000 h'],
          ['Temp range:',        '−20°C to +90°C'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={678} y={44+i*13} fontSize="9"
              fontWeight={l.includes('TOTAL')||l.includes('Efficiency:')?'700':'400'}
              fill={l.includes('TOTAL')||l.includes('Efficiency:')?'#c45a10':'#7a2e00'}>{l}</text>
            <text x={800} y={44+i*13} fontSize="9"
              fontFamily="'Courier New',monospace"
              fill={l.includes('TOTAL')||l.includes('Efficiency:')?'#c45a10':'#a04010'}>{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Ring pin rolling contact dominates losses · η is nearly constant across the full ratio range, unlike harmonic drives where efficiency drops at high ratios</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 8 — RV Reducer (compound planetary + cycloidal)
// Stage 1 panel: x=10 w=230. Arrow zone. Stage 2: x=255 w=340.
// Output arrow. Info panel: x=608 w=282 right=890 within W=900.
// ─────────────────────────────────────────────────────────────────────
function DiagramRV() {
  const W=900, H=310;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>RV Reducer — Compound Planetary + Cycloidal for Robot Joints (Axes 1–3)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arRV" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* Stage 1: Planetary — panel x=10 w=230 */}
        <rect x={10} y={15} width={230} height={284} rx={6} fill="none" stroke="#e8b07a" strokeDasharray="5,3" strokeWidth="1.5"/>
        <text x={125} y={32} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Stage 1: Planetary</text>
        <text x={125} y={43} textAnchor="middle" fontSize="8" fill="#a04010">Spur gears · 3–5:1 ratio</text>

        {(()=>{
          const cx=125, cy=170, rs=22, rp=14, rr=50;
          const pA=[90,210,330].map(d=>d*Math.PI/180);
          const pC=pA.map(a=>({x:cx+(rs+rp)*Math.cos(a),y:cy+(rs+rp)*Math.sin(a)}));
          return <g>
            <circle cx={cx} cy={cy} r={rr+12} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2"/>
            <circle cx={cx} cy={cy} r={rs} fill="#c45a10" opacity="0.85" stroke="#7a2e00" strokeWidth="2"/>
            <text x={cx} y={cy+4} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fdf4ec">Sun</text>
            {pC.map((p,i)=><g key={i}>
              <circle cx={p.x} cy={p.y} r={rp} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
              <circle cx={p.x} cy={p.y} r={rp*0.35} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1"/>
            </g>)}
            {pC.map((p,i)=><line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y}
              stroke="#a04010" strokeWidth="4" strokeLinecap="round" opacity="0.3"/>)}
            <circle cx={cx} cy={cy} r={rr} fill="none" stroke="#7a2e00" strokeWidth="1.2" opacity="0.35"/>
            <text x={cx} y={cy+rr+22} textAnchor="middle" fontSize="8" fill="#7a4a28">Input → sun gear</text>
            <text x={cx} y={cy+rr+33} textAnchor="middle" fontSize="8" fill="#7a4a28">Carrier → 3 cranks</text>
          </g>;
        })()}

        {/* Arrow Stage 1 → 2 */}
        <line x1={244} y1={155} x2={260} y2={155} stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arRV)"/>
        <text x={252} y={144} textAnchor="middle" fontSize="7.5" fill="#c45a10">3 cranks</text>

        {/* Stage 2: Cycloidal — panel x=265 w=328 */}
        <rect x={265} y={15} width={328} height={284} rx={6} fill="none" stroke="#c45a10" strokeDasharray="5,3" strokeWidth="2"/>
        <text x={429} y={32} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Stage 2: Cycloidal</text>
        <text x={429} y={43} textAnchor="middle" fontSize="8" fill="#c45a10">2 discs on 3 crankshafts · 30–160:1</text>

        {(()=>{
          const cx=429, cy=165, R=86, Rr=7, E=5, N=10, nPins=N+1;
          return <g>
            <circle cx={cx} cy={cy} r={R+14} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2.5"/>
            {Array.from({length:nPins},(_,i)=>{
              const a=(2*Math.PI*i)/nPins;
              return <circle key={i} cx={cx+R*Math.cos(a)} cy={cy+R*Math.sin(a)} r={Rr}
                fill="#d4874a" stroke="#7a2e00" strokeWidth="1.2"/>;
            })}
            <path d={cycloidPts(cx+E,cy,R,Rr,E,N)} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.8" opacity="0.72"/>
            <path d={cycloidPts(cx-E,cy,R,Rr,E,N,Math.PI)} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.8" opacity="0.55"/>
            {/* 3 crankshaft holes */}
            {[90,210,330].map((deg,i)=>{
              const a=deg*Math.PI/180, r=52;
              return <circle key={i} cx={cx+r*Math.cos(a)} cy={cy+r*Math.sin(a)} r={7}
                fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>;
            })}
            <circle cx={cx} cy={cy} r={5} fill="#7a2e00"/>
            <text x={cx} y={cy+R+24} textAnchor="middle" fontSize="8" fill="#7a4a28">3 crankshafts (○) drive both discs</text>
            <text x={cx} y={cy+R+35} textAnchor="middle" fontSize="8" fill="#7a4a28">Output = housing or disc pin</text>
          </g>;
        })()}

        {/* Arrow Stage 2 → output */}
        <line x1={596} y1={155} x2={612} y2={155} stroke="#7a2e00" strokeWidth="2.5" markerEnd="url(#arRV)"/>

        {/* Info panel: x=616 w=278 right=894 within W=900 */}
        <rect x={616} y={15} width={278} height={284} rx={5} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={755} y={32} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">RV Reducer Features</text>
        <line x1={624} y1={38} x2={886} y2={38} stroke="#e8b07a"/>
        {[
          ['Overall ratio:','i₁ × i₂'],
          ['','e.g. 4 × 43 = 172:1'],
          ['','4 × 161 = 644:1'],
          ['',''],
          ['Why 3 cranks?','Symmetric 120° loading'],
          ['','No net radial force'],
          ['','on main shaft'],
          ['','Higher stiffness &'],
          ['','moment capacity'],
          ['',''],
          ['Accuracy:','< 1 arc-min'],
          ['Backlash:','< 1 arc-min'],
          ['Stiffness:','Very high'],
          ['Moment load:','3–10× harmonic'],
          ['','(same OD)'],
          ['',''],
          ['Used in:','Robot axes 1–3'],
          ['','(heavy axes)'],
          ['Brands:','Nabtesco RV'],
          ['','Sumitomo Cyclo'],
          ['','SPINEA TwinSpin'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={624} y={50+i*11} fontSize="8.5" fontWeight={l?'700':'400'} fill={l?'#7a2e00':'#7a4a28'}>{l}</text>
            <text x={l?690:624} y={50+i*11} fontSize="8.5" fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>RV reducer = spur planetary pre-stage + cycloidal main stage · 3 crankshafts distribute load symmetrically, giving moment stiffness 3–10× higher than a single-eccentric design</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 9 — Applications (2×3 card grid)
// cardH=148 rowGap=22 H=2*148+22+18*2=376 → H=376
// ─────────────────────────────────────────────────────────────────────
function DiagramApplications() {
  const W=900, H=376;
  const apps=[
    {title:'Industrial Robot\nAxes 1–3',icon:'🤖',lines:['RV reducers standard','Heavy-load base/shoulder','Ratio 120–840:1','High moment stiffness']},
    {title:'Machine Tools\n& CNC',icon:'⚙️',lines:['Rotary tables, indexers','Near-zero backlash','Servo motor output','Sub-arcmin accuracy']},
    {title:'Conveyor &\nMixer Drives',icon:'🏭',lines:['Cyclo gearmotor series','5× shock load rating','IP65–IP69K sealed','Compact, long life']},
    {title:'AGV & Mobile\nRobotics',icon:'🚗',lines:['Wheel drive modules','High torque density','Backdriveable at low i','Compact inline design']},
    {title:'Aerospace\nActuators',icon:'✈️',lines:['Flight control surfaces','High shock tolerance','Redundant crankshafts','Lightweight designs']},
    {title:'Wind Turbine\nYaw & Pitch',icon:'🌬️',lines:['Blade pitch actuators','Nacelle yaw drives','Outdoor, long service','High torque, sealed']},
  ];
  const cardH=148, rowGap=22;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Cycloidal Drive Applications Across Engineering Disciplines</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {apps.map((a,i)=>{
          const col=i%3, row=Math.floor(i/3);
          const x=80+col*285, y=18+row*(cardH+rowGap);
          const lines=a.title.split('\n');
          return <g key={i}>
            <rect x={x-65} y={y} width={146} height={cardH} rx={6}
              fill={i%2===0?'#fdf4ec':'#fdf0e4'} stroke="#e8b07a" strokeWidth="1.5"/>
            <circle cx={x} cy={y+32} r={26} fill="#c45a10" opacity="0.85" stroke="#7a2e00" strokeWidth="2"/>
            <text x={x} y={y+39} textAnchor="middle" fontSize="18">{a.icon}</text>
            {lines.map((l,li)=>(
              <text key={li} x={x} y={y+68+li*14} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">{l}</text>
            ))}
            <line x1={x-55} y1={y+96} x2={x+55} y2={y+96} stroke="#e8c89a"/>
            {a.lines.map((l,li)=>(
              <text key={li} x={x} y={y+108+li*12} textAnchor="middle" fontSize="8" fill="#a04010">{l}</text>
            ))}
          </g>;
        })}
      </svg>
      <div style={S.dCaption}>Cycloidal drives dominate robot axes 1–3 (heaviest loads) · the Cyclo gearmotor is the world's best-selling industrial gearmotor for high-torque conveyor and mixer applications</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────
export default function CycloidalDrives() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Cycloidal Drive Systems in Mechanical Engineering</h1>
      <p style={S.p}>
        A <strong>cycloidal drive</strong> (cycloidal speed reducer) is a compact, high-ratio transmission in which an
        eccentric input shaft causes a cycloidal disc to orbit inside a ring of stationary pins. As the disc orbits, its
        lobes engage successive ring pins and the disc rotates backward at a greatly reduced speed. That slow rotation is
        extracted through output pins passing through oversized holes in the disc. Invented by Lorenz Braren in 1925 and
        commercialised as the <em>Cyclo Drive</em>, the technology achieves ratios of 3:1 to 7,569:1 (double stage),
        with approximately 50% of ring pins engaged simultaneously — giving exceptional shock-load tolerance, near-zero
        backlash, and a torque density that exceeds planetary gearboxes at high ratios.
      </p>

      <InfoBox title="Quick Reference — Core Facts &amp; Formulae" items={[
        "Five components: eccentric input shaft · cycloidal disc(s) · ring gear housing + stationary pins · output shaft + output pins · output pin holes",
        "Gear ratio:  i = −N_lobes  =  −(N_ring_pins − 1)  · negative = output reverses direction",
        "Example: 9 lobes, 10 ring pins → i = −9:1  · 59 lobes, 60 pins → −59:1",
        "Single-stage range: 3:1 to 119:1  · Double stage: up to 7,569:1 (87×87)",
        "Contact ratio: ~50% of ring pins in contact simultaneously (vs 10–30% harmonic, 1–2 teeth planetary)",
        "Shock load capacity: 5× rated torque (distributed multi-pin contact absorbs impulse)",
        "Single-stage efficiency: η ≈ 90–93%  · Double stage: ~86%  · Ratio-independent",
        "Output pin hole diameter: d_hole = d_output_pin + 2·E  (E = eccentricity in mm)",
        "Dual disc: two discs offset 180° on double-eccentric shaft — cancels imbalance, standard configuration",
        "RV reducer: spur planetary (3–5:1) + cycloidal stage = 120–840:1 for robot joint axes 1–3",
        "Design constraint: eccentricity E must be less than pin radius Rr for valid disc profile",
      ]}/>

      <h2 style={S.h2}>Complete Anatomy — Components &amp; Configuration</h2>
      <p style={S.p}>
        Unlike gear systems with involute flanks, the cycloidal drive uses rolling contact between the disc lobes and
        cylindrical ring pins. The input eccentric bearing causes the disc to <em>precess</em> — orbiting around the
        central axis while simultaneously rotating backward — and the output pins extract only the rotational component.
      </p>
      <DiagramAnatomy />
      <Table2 rows={[
        ['Eccentric input shaft','Offset from centre by eccentricity E (typically 2–10 mm)\nDrives the cycloidal disc in an orbital (not purely rotational) motion\nTwo eccentrics at 180° offset for dual-disc configuration'],
        ['Cycloidal disc','Profile is an epitrochoid — gives conjugate action with all ring pins simultaneously\nN lobes, always one fewer than the number of ring pins\nAll-rigid steel — no flexible element needed (unlike harmonic drives)'],
        ['Ring gear housing + pins','Stationary ring of N+1 cylindrical roller pins — fixed to gearbox housing\nPins are typically hardened ground steel or needle rollers (low friction)\n~50% of pins in contact at any input angle'],
        ['Output pins & holes','Output shaft carries pins that pass through oversized holes in the disc\nHole diameter = pin diameter + 2E (accounts for disc orbital radius E)\nPins are fixed to output shaft; disc holes orbit around them, extracting pure rotation'],
        ['Output shaft bearing','Main output bearing carries all radial and axial loads from the mechanism\nCross-roller bearings common in precision RV reducers for high moment capacity\nIsolated from cycloidal gearing — disc contact forces never reach output bearing directly'],
      ]}/>

      <h2 style={S.h2}>Operating Principle</h2>
      <p style={S.p}>
        The key insight is that the disc is not free to spin — it is constrained by the ring pins. Each time the
        eccentric pushes a disc lobe into the pin ring, the disc is forced to rotate backward by one lobe pitch.
        After one full input revolution the disc has rotated backward by exactly one lobe, giving a ratio equal to
        the lobe count.
      </p>
      <DiagramPrinciple />
      <Table2 rows={[
        ['Orbital + rotational motion','The disc centre orbits at radius E around the input axis\nSimultaneously the disc rotates about its own moving centre\nOutput pins extract only the rotational component — orbital motion is absorbed by hole clearance'],
        ['Per-revolution advancement','Input 360° CW → disc rotates (360°/N_lobes) CCW relative to housing\nFor N=8: each input revolution → 45° disc backward rotation\nFor N=8: 8 input revolutions needed for one full output revolution → 8:1'],
        ['Direction convention','Output always opposes input direction (negative ratio) when housing is fixed\nIf housing rotates and disc is fixed: same direction as input, i = N_ring/N_lobes\nDouble stage: two reversals cancel → output same direction as input'],
        ['Lobe engagement sequence','At any position, ~N/2 lobes contact ring pins simultaneously\nAs input rotates, contact transfers smoothly from pin to pin with no impact or dwell\nThis continuous hand-off is why cycloidal drives are quiet and handle shock well'],
        ['Output pin extraction','While disc orbits + rotates, output shaft pins inside disc holes trace epicyclic paths\nPins are rigidly connected to output shaft, constrained to pure rotation\nThe difference between disc orbit and rotation is extracted as the output'],
      ]}/>

      <h2 style={S.h2}>Disc Profile Geometry</h2>
      <DiagramGeometry />
      <Table2 rows={[
        ['Epitrochoid basis','Profile is an epitrochoid — a generalised cycloid from rolling-circle geometry\nThe parametric equations give the unique curve conjugate to all ring pin positions\nNo other profile shape achieves this simultaneous multi-pin conjugate action'],
        ['Key parameters','R = ring pin pitch circle radius · Rr = pin roller radius · E = eccentricity\nN = number of lobes (= number of ring pins − 1) · θ = input angle parameter'],
        ['Eccentricity constraint','E < Rr is required for a smooth, undercut-free profile\nIf E ≥ Rr: lobes become pointed → stress concentration and short life\nTypical E/Rr ratio: 0.3–0.7 for good geometry; 0.5 is a common design starting point'],
        ['Shortened (reduced) cycloid','Profile multiplied by shortening factor K < 1 (typically 0.7–0.95)\nReduces lobe depth → lower eccentricity → less vibration and inertia at speed\nSlightly reduces contact ratio and load capacity — preferred in high-speed designs'],
        ['Output pin hole sizing','d_hole = d_output_pin + 2·E  — this is an exact relationship, not an approximation\nIf clearance < 2E: interference → binding during orbit\nIf clearance > 2E: lost motion in hole → effective backlash in output'],
      ]}/>

      <h2 style={S.h2}>Gear Ratio Calculation</h2>
      <DiagramRatio />
      <Table2 rows={[
        ['Single-stage ratio','i = −N_lobes = −(N_ring_pins − 1)\nNegative sign: output reverses relative to input (housing fixed)\nRange: 3:1 to 119:1 commercially (N_lobes = 3 to 119)'],
        ['Double-stage ratio','i_total = i₁ × i₂ = N₁ × N₂  (both minus signs cancel → same direction as input)\nMaximum: 7,569:1 = 87 × 87  — commercially available from Sumitomo\nBoth stages coaxial; overall same direction output despite two reversals'],
        ['Size independence','Ratio is determined only by N_lobes — not by disc diameter or pin size\nSame housing can host different ratios by changing disc and pin count\nAdding one lobe (and one ring pin) increases ratio by exactly 1'],
        ['Comparison with planetary','Planetary single stage: 3:1 to ~10:1 practical maximum\nCycloidal single stage: 3:1 to 119:1 — ~10× more range in the same envelope\nFor ratios > 30:1 cycloidal is always more compact and more efficient than multi-stage planetary'],
        ['Effect on output torque','τ_out = τ_in × N_lobes × η\nHigher ratio → proportionally more output torque in ideal case\nReal torque limited by rated disc stress and ring pin bearing life'],
        ['Back-driveability','At low ratios (< 30:1) cycloidal may be back-driven (output forces input)\nAt high ratios (> 60:1) self-locking tendency increases with friction coefficient\nNot inherently self-locking like worm gears — friction determines the threshold'],
      ]}/>

      <h2 style={S.h2}>Dual-Disc Configuration &amp; Balancing</h2>
      <DiagramDualDisc />
      <Table2 rows={[
        ['Why two discs?','Single eccentric creates imbalance force F = m_disc × E × ω_in² — grows with speed²\nThis centrifugal force causes vibration at input frequency and limits max rpm\nDual discs at 180° phase cancel imbalance → smooth, high-speed operation'],
        ['Double eccentric shaft','One input shaft with two eccentric lobes offset by exactly 180°\nDisc 1 at phase 0°, Disc 2 at phase 180° — always diametrically opposite\nOutput pins pass through both discs → double torque capacity for the same OD'],
        ['Three-disc variant','Used for very high-speed inputs (> 3000 rpm)\nMiddle disc has twice the mass of each outer disc\nOuter discs move in unison, opposite to middle → both static and dynamic balance achieved'],
        ['Crankshaft variant (RV-style)','Three crankshafts (each a separate eccentric) at 120° spacing\nDriven by the planetary pre-stage carrier, not by a single eccentric shaft\nHigher load capacity, stiffness, and moment resistance per unit volume'],
        ['Effect on torque capacity','Two discs share the load 50/50 → each disc carries half the rated torque\nAllows the same rated output torque from a smaller disc and housing\nOr: double the torque from the same housing compared to a single disc'],
      ]}/>

      <h2 style={S.h2}>Multi-Tooth Contact &amp; Load Sharing</h2>
      <DiagramLoadShare />
      <Table2 rows={[
        ['Contact ratio','~50% of ring pins (N/2) carry load simultaneously at any disc position\nThis is the defining advantage — each pin carries only 2/N of the total load\nCompare: planetary 1–3 teeth; harmonic 10–30% of teeth but smaller contact area'],
        ['Load per ring pin','F_pin = τ_output / (N/2 × R_pin_circle)\nWith N=10: each pin carries 1/5 of total tangential force\nSmall per-pin forces → low Hertz stress → long rolling contact fatigue life'],
        ['Shock load capacity','5× rated torque tolerable without damage (instantaneous shock)\nDistributed load means no single pin is critically loaded under impact\nCompare: planetary ~2× rated · harmonic ~3× rated'],
        ['Contact geometry','Rolling contact between disc lobe and cylindrical pin → Hertz line contact stress\nCylindrical rolling is inherently lower stress than involute flank sliding contact\nContact area proportional to pin radius and disc wall thickness (axial direction)'],
        ['Force distribution','Each contact force vector points approximately radially inward\nNet torque = sum of tangential components of all contact forces\nRadial components cancel due to symmetry → no net side load on input eccentric shaft'],
        ['Manufacturing sensitivity','Profile errors cause unequal load sharing among pins\nTighter tolerances improve sharing — JIS Grade 5–6 industrial, Grade 3–4 robot precision\nTolerance stack: disc profile error + housing pin position + bearing clearance all contribute'],
      ]}/>

      <h2 style={S.h2}>Efficiency &amp; Power Loss</h2>
      <DiagramEfficiency />
      <Table2 rows={[
        ['Overall efficiency','Single stage: η ≈ 90–93%  · Double stage: η ≈ 86%  · Triple stage: η ≈ 80%\nBetter than harmonic drives (75–85%); slightly below planetary (94–98%)\nNearly constant across the full ratio range — unlike harmonic where η drops at high ratios'],
        ['Ring pin friction (dominant)','Rolling contact with friction coefficient μ ≈ 0.01–0.05 (lubricated)\nLoss = Σ(F_contact × μ × R_pin) summed over all ~N/2 engaged pins\nReducible by needle roller ring pins (μ ≈ 0.003–0.01) vs plain cylindrical pins'],
        ['Eccentric bearing loss','Input bearing under oscillating eccentric radial load at ω_in frequency\nUse cylindrical roller or needle bearing — not deep-groove ball — for this loading\nBearing pre-load and alignment significantly affect both losses and bearing life'],
        ['Output pin friction','Pins slide tangentially in disc holes as disc orbits at radius E\nSmall sliding velocity = E × ω_in × (ratio correction)\nNeedle rollers on output pins reduce this loss significantly in precision designs'],
        ['Lubrication','Oil bath: ISO VG 150–320 gear oil, fill to 1/3 of housing volume\nSealed units: NLGI No. 2 lithium-based or synthetic (PAO) grease\nChange interval: 20,000 h (mineral) · 30,000 h (synthetic)\nOver-fill causes churning loss + temperature rise; under-fill causes pin wear'],
        ['Temperature effects','Operating range: −20°C to +90°C standard; −40°C to +80°C with synthetic lubricant\nAt low temperature: grease stiffness increases → warm-up period recommended\nAt high temperature: oil viscosity drops → thinner EHD film → accelerated pin wear risk'],
      ]}/>

      <h2 style={S.h2}>RV Reducer — Compound Design for Robot Joints</h2>
      <p style={S.p}>
        The <strong>RV (Rotate Vector) reducer</strong> is the dominant transmission for robot joint axes 1, 2, and 3
        — the axes that carry the largest loads and moment arms. It combines a spur planetary pre-stage (3–5:1) with a
        cycloidal main stage driven by three crankshafts, achieving overall ratios of 120:1 to 840:1 with exceptional
        torsional stiffness and moment load capacity.
      </p>
      <DiagramRV />
      <Table2 rows={[
        ['RV architecture','Stage 1: simple spur planetary gears (sun + 3 planets + fixed ring)\nPlanetary carrier connects to 3 crankshafts at 120° spacing (not a single eccentric)\nStage 2: two cycloidal discs driven simultaneously by all 3 crankshafts'],
        ['Three-crankshaft advantage','Load distributed equally across 3 cranks → 1/3 force each crankshaft bearing\nSymmetric 120° arrangement → no net radial force on the output shaft\nMuch higher torsional and moment stiffness than single-eccentric designs'],
        ['Overall ratio','i_total = i_planetary × i_cycloidal = (3–5) × (31–160) = 120–840:1\nExamples: Nabtesco RV-6E: 57, 103, 161:1  · RV-20N: 57–161:1\nRatio selected based on motor speed and required joint speed of robot arm'],
        ['Accuracy and backlash','< 1 arc-min standard · < 0.5 arc-min precision grade\nSet by bearing pre-load adjustment during factory assembly\nRe-measurable in field — critical specification for robot path accuracy'],
        ['Moment load capacity','Main output cross-roller bearing handles combined radial + axial + moment loads\nMoment capacity 3–10× higher than equivalent harmonic drive (same OD)\nThis is why RV is used for axes 1–3 — largest cantilever moment arms on the robot'],
        ['Maintenance and failure','Primary wear site: crankshaft cylindrical roller bearings (alternating load fatigue)\nOil change: 20,000 h  · Crankshaft bearing replacement: 10,000–20,000 h\nMonitoring: vibration signature at crankshaft frequency + periodic oil particle analysis'],
      ]}/>

      <h2 style={S.h2}>Design Selection Procedure</h2>
      <Table2 rows={[
        ['Step 1 — Load definition','Rated torque, peak torque, duty cycle, starts/hour, shock factor K_s ≥ 1.0–3.0\nτ_required = τ_application × K_s — must not exceed catalog peak rating\nAlso define: input speed, output speed, mounting orientation, ambient temperature'],
        ['Step 2 — Ratio selection','i = ω_input / ω_output (round to nearest standard ratio)\nStandard single-stage ratios: 6, 9, 11, 17, 25, 35, 43, 59, 87, 119\nDouble stage if i > 119; RV reducer if moment load is critical and i = 120–840'],
        ['Step 3 — Frame size','Select from catalog based on rated output torque at the required output speed\nVerify: average torque < rated · peak torque < momentary peak allowable\nCheck: L10 bearing life ≥ required service life at the average load and speed'],
        ['Step 4 — Geometry check','Confirm input shaft runout < 0.02 mm TIR (affects eccentric bearing load)\nCheck output flange bolt circle — use all bolt positions at correct torque\nFor RV: verify planetary sun gear mesh and crankshaft bearing pre-load settings'],
        ['Step 5 — Thermal','P_loss = P_in × (1−η) → continuous heat generation\nIf ΔT > 50°C above ambient: add cooling fin, oil cooling loop, or derate the load\nFor vertical mounting: check oil level specification (different from horizontal)'],
        ['Step 6 — Lubrication','Horizontal: ISO VG 150 oil bath at 1/3 fill level\nVertical input-up or down: ISO VG 220, higher fill level per catalog\nFor sealed precision units: specified synthetic grease type and fill mass (not volume)'],
        ['Step 7 — Integration','Align flexible input coupling to within 0.05 mm TIR to prevent eccentric overload\nOutput flange: use all bolt holes, calibrated torque wrench, thread-lock as specified\nFor robot joints: measure backlash after assembly and adjust pre-loads until in spec'],
      ]}/>

      <h2 style={S.h2}>Failure Modes &amp; Maintenance</h2>
      <Table2 rows={[
        ['Eccentric bearing fatigue','Most common end-of-life failure — alternating radial load at ω_in on CRB\nSymptoms: vibration increase at input frequency, rising operating temperature\nPrevention: correct oil fill, avoid shaft misalignment, do not exceed rated speed'],
        ['Ring pin surface fatigue','Cyclic Hertz contact → pitting on pin surface over very long service\nProgressive pin wear increases backlash over the service life\nPrevention: adequate lubrication viscosity, avoid sustained torque overload'],
        ['Output pin hole wear','Fretting and sliding between output pins and disc holes during orbit\nResults in increased backlash and growing position error\nPrevention: correct hole clearance (= 2E + small running clearance), clean oil'],
        ['Disc lobe profile wear','Gradual material loss at the lobe-pin contact — more severe at low pin counts\nDetection: increased backlash + changes in vibration signature spectral content\nPrevention: verified lubrication, avoid shock loads exceeding 5× rated torque'],
        ['Lubricant degradation','Water ingress → emulsification → corrosion of rolling surfaces → rapid failure\nParticle contamination creates abrasive wear loop — exponential degradation\nPrevention: shaft seal inspection every 4000 h; oil sampling in critical applications'],
        ['Inspection schedule','Vibration monitoring: track crankshaft bearing defect frequencies with FFT\nOil analysis: check viscosity, water content, metal particle count\nBacklash check: at commissioning and every 4000–8000 service hours\nComplete rebuild: at or before calculated L10 bearing life of crankshaft bearings'],
      ]}/>

      <h2 style={S.h2}>Applications</h2>
      <DiagramApplications />
      <Table2 rows={[
        ['Industrial robotics','Axes 1–3 of 6-axis robots: RV reducers standard (Nabtesco, Sumitomo, SPINEA)\nAxes 4–6: harmonic drives preferred (lighter, hollow shaft, lower torque needed)\nTypically 3 RV + 3 harmonic drives per 6-axis industrial robot'],
        ['Cyclo gearmotor (industrial)','World-leading Sumitomo Cyclo 6000 gearmotor family\nConveyors, mixers, extruders, cranes, elevators, hoists\nRatios 6:1–7,569:1 · Power range 0.1–150 kW · IEC frame motors'],
        ['Machine tool rotary tables','4th/5th-axis CNC tables: zero backlash for precision angular indexing\nHigh moment stiffness supports cutting forces on heavy workpieces\nTypical: servo motor + cycloidal reducer + cross-roller output bearing'],
        ['Mobile robotics & AGV','Wheel drive modules: high torque density in compact hub-integrated package\nShock tolerance for rough floor surfaces, kerbs, and collision events\nLow-to-moderate ratios (30–60:1) allow useful backdrive compliance'],
        ['Wind turbine drives','Blade pitch: 3 drives per turbine, slow-speed high-torque outdoor duty\nNacelle yaw: 4–8 drives slewing nacelle against wind loads\nSealed, synthetic-oil filled for −40°C to +60°C temperature range'],
        ['Precision positioning','SPINEA TwinSpin: cycloidal + cross-roller in single ultra-compact unit\nElectronic assembly machines, PCB drilling, semiconductor wafer handling\nArcminute-class backlash · torsional stiffness > 1000 N·m/arcmin'],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Gear ratio:  i = −N_lobes  =  −(N_ring_pins − 1)  · magnitude = lobe count · sign = reversal",
        "Double stage:  i_total = N₁ × N₂  (two reversals cancel → same direction as input)",
        "Output speed:  ω_out = ω_in / N_lobes  (magnitude, opposite direction)",
        "Output torque:  τ_out = τ_in × N_lobes × η  (η ≈ 0.90–0.93 single stage)",
        "Output pin hole:  d_hole = d_output_pin + 2·E  (E = eccentricity in mm)",
        "Profile X(θ):  R·cos(θ) − Rr·cos(θ+ψ) − E·cos(N·θ)",
        "Profile Y(θ):  −R·sin(θ) + Rr·sin(θ+ψ) + E·sin(N·θ)",
        "Auxiliary angle:  ψ = arctan( sin((1−N)θ) / ( R/(E(N+1)) − cos((1−N)θ) ) )",
        "Eccentricity constraint:  E < Rr  (required for valid undercut-free profile)",
        "Contact ratio:  ≈ N/2 pins simultaneously  (≈ 50% of all ring pins at all times)",
        "Imbalance force (single disc):  F_imb = m_disc × E × ω_in²  → cancelled by dual-disc",
        "RV overall ratio:  i_RV = i_planetary × i_cycloidal  =  (3–5) × (31–160)  = 120–840:1",
      ]}/>
    </div>
  );
}